import { useContext } from "react";
import {
  Group,
  Text,
  Progress,
  ActionIcon,
  ThemeIcon,
  Loader,
  Box,
  Grid,
  Tooltip,
} from "@mantine/core";
import {
  IconX,
  IconRefresh,
  IconAlertCircle,
  IconUpload,
  IconFolder,
  IconVideo,
  IconCheck,
  IconChevronRight,
  IconTrash,
} from "@tabler/icons-react";
import { fileSizeToString, timeAgo, UPLOAD_STATUSES } from "../utils/constants";
import classes from "../styles/ActiveUploadItem.module.css";
import { UploadContext } from "../contexts/UploadContext";

function ActiveUploadItem({ upload }) {
  const {
    fileName: title,
    file: { size },
    status,
    progress,
    folderName: destination,
    errorMsg,
    clientId,
    completedAt,
  } = upload;
  const { cancelUpload, retryUpload, clearCompletedUpload } =
    useContext(UploadContext);

  const getStatusContent = () => {
    switch (status) {
      case UPLOAD_STATUSES.QUEUED.value:
        return (
          <Group align="center" justify="space-between">
            <Text size="xs" c="dimmed">
              {UPLOAD_STATUSES.QUEUED.label}
            </Text>
          </Group>
        );
      case UPLOAD_STATUSES.UPLOADING.value:
        return (
          <>
            <Progress value={progress} color="teal" size="sm" mt={5} mb={4} />
            <Group align="center" justify="space-between" mt={4}>
              <Group gap={6}>
                <ThemeIcon
                  size="xs"
                  radius="xl"
                  variant="transparent"
                  c="dimmed"
                  className={classes.pulse}
                >
                  <IconUpload size={18} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {UPLOAD_STATUSES.UPLOADING.label} {progress}%
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {fileSizeToString(size)}
              </Text>
            </Group>
          </>
        );
      case UPLOAD_STATUSES.MOVING_TO_PROCESSING.value:
        return (
          <Group align="center" justify="space-between">
            <Group gap={6}>
              <Loader size={13} />
              <Text size="xs" align="left" c="dimmed" className={classes.pulse}>
                {UPLOAD_STATUSES.MOVING_TO_PROCESSING.label}
              </Text>
            </Group>
          </Group>
        );
      case UPLOAD_STATUSES.WAITING_TO_PROCESS.value:
        return (
          <Group align="center" justify="space-between">
            <Group gap={6}>
              <Loader size={13} />
              <Text size="xs" align="left" c="dimmed" className={classes.pulse}>
                {UPLOAD_STATUSES.WAITING_TO_PROCESS.label}
              </Text>
            </Group>
          </Group>
        );
      case UPLOAD_STATUSES.PROCESSING.value:
        return (
          <Group align="center" justify="space-between">
            <Group gap={6}>
              <ThemeIcon
                size="xs"
                radius="xl"
                variant="transparent"
                c="dimmed"
                className={classes.rotateAnimation}
              >
                <IconRefresh
                  size={18}
                  style={{
                    transform: "rotateX(180deg)",
                  }}
                />
              </ThemeIcon>
              <Text size="xs" c="dimmed" className={classes.pulse}>
                {UPLOAD_STATUSES.PROCESSING.label}
              </Text>
            </Group>
          </Group>
        );
      case UPLOAD_STATUSES.ANALYSING_VIDEO.value:
        return (
          <Group align="center" gap={6}>
            <Loader size={13} />
            <Text size="xs" c="dimmed" className={classes.pulse}>
              {UPLOAD_STATUSES.ANALYSING_VIDEO.label}
            </Text>
          </Group>
        );
      case UPLOAD_STATUSES.STORING_RESULTS.value:
        return (
          <Group align="center" gap={6}>
            <Loader size={13} />
            <Text size="xs" c="dimmed" className={classes.pulse}>
              {UPLOAD_STATUSES.STORING_RESULTS.label}
            </Text>
          </Group>
        );
      case UPLOAD_STATUSES.FAILED_TO_PROCESS.value:
        return (
          <Group align="flex-end" gap={4} wrap="nowrap">
            <ThemeIcon size="xs" radius="xl" variant="transparent" color="red">
              <IconAlertCircle size={14} />
            </ThemeIcon>
            <Tooltip label={errorMsg || "Unknown error"} withArrow>
              <Text size="xs" c="red" lineClamp={1}>
                {UPLOAD_STATUSES.FAILED_TO_PROCESS.label} •{" "}
                {errorMsg || "Unknown error"}
              </Text>
            </Tooltip>
          </Group>
        );
      case UPLOAD_STATUSES.UPLOAD_FAILED.value:
        return (
          <Group align="flex-end" gap={4} wrap="nowrap">
            <ThemeIcon size="xs" radius="xl" variant="transparent" color="red">
              <IconAlertCircle size={14} />
            </ThemeIcon>
            <Tooltip label={errorMsg || "Unknown error"} withArrow>
              <Text
                size="xs"
                c="red"
                lineClamp={1}
                style={{ wordBreak: "break-all" }}
              >
                {UPLOAD_STATUSES.UPLOAD_FAILED.label} •{" "}
                {errorMsg || "Unknown error"}
              </Text>
            </Tooltip>
          </Group>
        );
      case UPLOAD_STATUSES.CANCELLING.value:
        return (
          <Group align="center" gap={4}>
            <Text size="xs" c="dimmed" className={classes.pulse}>
              {UPLOAD_STATUSES.CANCELLING.label}
            </Text>
          </Group>
        );
      case UPLOAD_STATUSES.COMPLETE.value:
        return (
          <Group align="center" gap={4} wrap="nowrap">
            <ThemeIcon size="xs" radius="xl" variant="transparent" color="teal">
              <IconCheck size={18} />
            </ThemeIcon>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {UPLOAD_STATUSES.COMPLETE.label}{" "}
              <Text span c="dimmed">
                • File moved to processing
              </Text>
            </Text>
          </Group>
        );
      default:
        return null;
    }
  };

  return (
    <Grid align="center" px="sm">
      <Grid.Col span="content">
        <ThemeIcon variant="light" color="teal" size={48} radius={4}>
          <IconVideo size={18} />
        </ThemeIcon>
      </Grid.Col>

      <Grid.Col span="auto">
        <Tooltip
          label={
            <Group gap={4} align="center" wrap="nowrap">
              <IconFolder size={16} />
              <Text size="sm" fw={500}>
                {destination}
              </Text>
              <IconChevronRight size={16} />
              <Text size="sm" lineClamp={1}>
                {title}
              </Text>
            </Group>
          }
          withArrow
          position="top"
          openDelay={500}
        >
          <Group gap={4} wrap="nowrap" align="center">
            <ThemeIcon
              size="xs"
              radius="xl"
              variant="transparent"
              color="default"
            >
              <IconFolder />
            </ThemeIcon>
            <Text size="sm" fw={500} lineClamp={1}>
              {destination}
            </Text>
            <ThemeIcon
              size="xs"
              radius="xl"
              variant="transparent"
              color="default"
            >
              <IconChevronRight />
            </ThemeIcon>
            <Text size="sm" lineClamp={1}>
              {title}
            </Text>
          </Group>
        </Tooltip>
        <Box mt={1}>{getStatusContent()}</Box>
      </Grid.Col>

      <Grid.Col span="content">
        <Group justify="flex-end" gap={4} wrap="nowrap">
          {status === UPLOAD_STATUSES.COMPLETE.value && completedAt && (
            <Text size="xs" c="dimmed" mr="md">
              {timeAgo(completedAt)}
            </Text>
          )}
          {status === UPLOAD_STATUSES.UPLOAD_FAILED.value && (
            <Tooltip label="Retry" withArrow position="top" openDelay={500}>
              <ActionIcon
                size="sm"
                color="default"
                variant="subtle"
                onClick={() => retryUpload(clientId)}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {status === UPLOAD_STATUSES.COMPLETE.value ? (
            <Tooltip label="Clear" withArrow position="top" openDelay={500}>
              <ActionIcon
                size="sm"
                color="default"
                variant="subtle"
                onClick={() => clearCompletedUpload(clientId)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          ) : (
            <Tooltip label="Cancel" withArrow position="top" openDelay={500}>
              <ActionIcon
                size="sm"
                color="default"
                variant="subtle"
                onClick={() => cancelUpload(clientId)}
                disabled={
                  status === UPLOAD_STATUSES.MOVING_TO_PROCESSING.value ||
                  status === UPLOAD_STATUSES.CANCELLING.value
                }
              >
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Grid.Col>
    </Grid>
  );
}

export default ActiveUploadItem;
